import { memo, useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Film, ImageDown, MonitorPlay, Pause, Play, Radio, StepBack, StepForward } from 'lucide-react';

import { Callout, CopyAction, Empty, ErrorCallout, Id, Lamp, PageHead, Segmented, Spinner } from '../components/ui';
import { api, ApiError, asApiError } from '../lib/api';
import { duration, fullTime, plural, shortId, timecode } from '../lib/format';
import { useDocumentTitle, useNow } from '../lib/hooks';
import {
  appendEntries,
  buildTimeline,
  caption,
  DIRS,
  download,
  entryAt,
  exportGif,
  exportName,
  exportVideo,
  fromPacked,
  frameUrl,
  hasFrame,
  marksAt,
  runSummary,
  stepAction,
  stepLabel,
  timelineEnd,
  toPacked,
  VERDICT_TONE,
  verdictLabel,
  videoType,
  type FrameBearing,
  type Mark,
  type Timeline,
} from '../lib/replay';
import type { Recording, RecordingEntry, RecordingSummary } from '../lib/types';

/* The replay viewer, /r/:id: one GUI sandbox's or task's screen as the
   plane recorded it. Live over SSE while it records, a scrub-and-step replay
   after, the agent's clicks marked on its frames, and GIF and video exports
   drawn from the same pictures. */

/* -------------------------------------------------------------- links */

const replayPath = (id: string) => `/r/${encodeURIComponent(id)}`;

/** A detail page's way in: "Watch live" while the work runs, "Replay" after. */
export function ReplayLink({ id, live }: { id: string; live: boolean }) {
  return (
    <Link className="btn" to={replayPath(id)}>
      {live ? <Lamp tone="live" /> : <MonitorPlay aria-hidden="true" />}
      {live ? 'Watch live' : 'Replay'}
    </Link>
  );
}

/** The same, small, for a list row. */
export function ReplayChip({ id, live }: { id: string; live: boolean }) {
  return (
    <Link className="replay-chip" to={replayPath(id)} onClick={(e) => e.stopPropagation()} title={live ? 'Watch its screen live' : 'Replay its screen'}>
      <MonitorPlay aria-hidden="true" />
      {live ? 'Live' : 'Replay'}
    </Link>
  );
}

/* --------------------------------------------------------------- data */

/**
 * GET the recording, then, while it records, follow the live stream, opening
 * a new one after the last entry when one that worked closes. If a stream
 * never opens, poll the recording every 3 s while the tab is visible, until
 * it ends.
 */
function useRecording(id: string) {
  const path = `/v1/recordings/${encodeURIComponent(id)}`;
  const [rec, setRec] = useState<Recording>();
  const [error, setError] = useState<ApiError>();
  const [follow, setFollow] = useState<'stream' | 'poll'>('stream');
  // Bumped to open a new stream after one that worked closed: the plane ends each after 10 minutes.
  const [streams, setStreams] = useState(0);
  const recRef = useRef(rec);
  recRef.current = rec;

  const load = useCallback(async () => {
    try {
      setRec(await api<Recording>(path));
      setError(undefined);
    } catch (err) {
      setError(asApiError(err));
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  const recording = rec?.state === 'recording';

  useEffect(() => {
    if (!recording || follow !== 'stream') return;
    const last = recRef.current?.entries.at(-1)?.seq;
    const source = new EventSource(`${path}/live${last !== undefined ? `?after=${last}` : ''}`);
    // The backlog can arrive as a burst: add entries in batches.
    let queue: RecordingEntry[] = [];
    let timer = 0;
    const flush = () => {
      window.clearTimeout(timer);
      timer = 0;
      const batch = queue;
      queue = [];
      if (batch.length) setRec((prev) => (prev ? appendEntries(prev, batch) : prev));
    };
    source.addEventListener('entry', (e) => {
      try {
        queue.push(JSON.parse((e as MessageEvent<string>).data) as RecordingEntry);
      } catch {
        return;
      }
      if (!timer) timer = window.setTimeout(flush, 80);
    });
    source.addEventListener('end', (e) => {
      flush();
      source.close();
      let summary: Partial<RecordingSummary> = {};
      try {
        summary = JSON.parse((e as MessageEvent<string>).data) as RecordingSummary;
      } catch {
        // The entries already here are the recording; only its totals are missing.
      }
      setRec((prev) => (prev ? { ...prev, ...summary, state: 'ended', entries: prev.entries } : prev));
    });
    let opened = false;
    source.onopen = () => {
      opened = true;
    };
    source.onerror = () => {
      flush();
      source.close();
      // A stream that worked picks up after its last entry; one that never opened gives way to polling.
      if (opened) window.setTimeout(() => setStreams((n) => n + 1), 1000);
      else setFollow('poll');
    };
    return () => {
      window.clearTimeout(timer);
      source.close();
    };
  }, [recording, follow, path, streams]);

  useEffect(() => {
    if (!recording || follow !== 'poll') return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [recording, follow, load]);

  return { rec, error, reload: load };
}

/* --------------------------------------------------------------- page */

export function ReplayPage() {
  const { id = '' } = useParams();
  // Everything (position, stream, exports) starts over for another recording.
  return <Replay key={id} id={id} />;
}

function Replay({ id }: { id: string }) {
  const { rec, error, reload } = useRecording(id);
  const kind = rec?.kind ?? (id.startsWith('task_') ? 'task' : 'sandbox');
  const listPath = kind === 'task' ? '/ci' : '/sandboxes';
  const crumbs = <Link to={listPath}>{kind === 'task' ? 'CI' : 'Sandboxes'}</Link>;
  useDocumentTitle(rec ? `Replay of ${rec.name ?? shortId(rec.id)}` : 'Replay');
  const live = rec?.state === 'recording';
  const now = useNow(live ? 1000 : 60_000);
  const entries = rec?.entries;
  const startedAt = rec?.startedAt;
  const tl = useMemo(() => (entries && startedAt !== undefined ? buildTimeline(entries, startedAt) : null), [entries, startedAt]);

  if (!rec || !tl) {
    return (
      <>
        <PageHead title="Replay" crumbs={crumbs} />
        {error ? (
          <div className="band">
            {error.status === 404 ? (
              <Empty title="No recording">
                <p>
                  <code>{id}</code> has no recording on this team. Sandboxes created with <code>record: false</code> have none, and neither do headless ones: only a screen is recorded.
                </p>
                <p>If someone shared this link, they may be on another team: switch teams, or ask them to export a GIF.</p>
                <Link className="btn" to={`${listPath}/${encodeURIComponent(id)}`}>
                  {kind === 'task' ? 'Open the task' : 'Open the sandbox'}
                </Link>
              </Empty>
            ) : (
              <ErrorCallout error={error} onRetry={() => void reload()} />
            )}
          </div>
        ) : (
          <div className="rp" aria-busy="true">
            <span className="skeleton rp-skeleton" />
            <span className="skeleton rp-skeleton" />
          </div>
        )}
      </>
    );
  }

  const end = timelineEnd(rec, tl, now);
  const lastFrame = [...rec.entries].reverse().find(hasFrame);

  return (
    <>
      <PageHead
        crumbs={crumbs}
        title={rec.name ?? shortId(rec.id)}
        lede={<RecordingMeta rec={rec} end={end} />}
        actions={<Exports rec={rec} tl={tl} end={end} />}
      />
      {error ? (
        <div className="band band--tight">
          <ErrorCallout error={error} title="Live updates stopped" onRetry={() => void reload()} />
        </div>
      ) : null}
      {rec.truncated ? (
        <div className="band band--tight">
          <Callout tone="warn" title="This recording stopped keeping frames">
            It reached its frame cap{lastFrame ? ` at +${timecode(tl.offsets[rec.entries.indexOf(lastFrame)])}` : ''}, so later moments show the last frame kept. Steps, commands and logs were still recorded.
          </Callout>
        </div>
      ) : null}
      <Viewer rec={rec} tl={tl} end={end} />
    </>
  );
}

function RecordingMeta({ rec, end }: { rec: Recording; end: number }) {
  const runs = useMemo(() => runSummary(rec.entries), [rec.entries]);
  const live = rec.state === 'recording';
  const detail = `${rec.kind === 'task' ? '/ci' : '/sandboxes'}/${encodeURIComponent(rec.id)}`;
  const last = runs.last;
  return (
    <span className="sbx-meta">
      <span className={`pill${live ? '' : ' pill--muted'}`}>
        <Lamp tone={live ? 'live' : 'off'} />
        {live ? 'Recording' : 'Ended'}
      </span>
      {runs.running && live ? (
        <span className="pill">
          <Lamp tone="live" />
          Agent running
        </span>
      ) : last ? (
        <span className={`pill${last.verdict === 'failed' || (!last.verdict && last.error) ? ' pill--failed' : ''}`} title={last.reason ?? last.error?.message}>
          <Lamp tone={VERDICT_TONE[last.verdict ?? 'error']} />
          {verdictLabel(last)}
        </span>
      ) : null}
      <span className="rp-meta__id">
        {rec.kind === 'task' ? 'Task' : 'Sandbox'} <Id id={rec.id} to={detail} />
      </span>
      <span title={`Started ${fullTime(rec.startedAt)}${rec.endedAt ? `, ended ${fullTime(rec.endedAt)}` : ''}`}>{duration(end)}</span>
      <span>{plural(rec.frames, 'frame')}</span>
    </span>
  );
}

/* ------------------------------------------------------------ exports */

function Exports({ rec, tl, end }: { rec: Recording; tl: Timeline; end: number }) {
  const [job, setJob] = useState<{ kind: 'gif' | 'video'; progress: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  const canVideo = useMemo(() => videoType() !== null, []);
  const any = useMemo(() => rec.entries.some(hasFrame), [rec.entries]);

  async function run(kind: 'gif' | 'video') {
    const control = new AbortController();
    abort.current = control;
    setJob({ kind, progress: 0 });
    setError(null);
    const options = { signal: control.signal, onProgress: (progress: number) => setJob({ kind, progress }) };
    try {
      // What's recorded at the moment of the click: a live recording keeps going without it.
      if (kind === 'gif') {
        download(await exportGif(rec, tl, end, options), `${exportName(rec)}.gif`);
      } else {
        const { blob, ext } = await exportVideo(rec, tl, end, options);
        download(blob, `${exportName(rec)}.${ext}`);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (abort.current === control) abort.current = null;
      setJob(null);
    }
  }

  return (
    <>
      {job ? (
        <span className="rp-export" role="status">
          <Spinner size={13} />
          <span>
            {job.kind === 'gif' ? 'Making the GIF' : 'Recording the video, keep this tab open'} {Math.round(job.progress * 100)}%
          </span>
          <span className="rp-export__bar" aria-hidden="true">
            <span style={{ width: `${job.progress * 100}%` }} />
          </span>
          <button type="button" className="btn btn--sm btn--quiet" onClick={() => abort.current?.abort()}>
            Cancel
          </button>
        </span>
      ) : (
        <>
          <button type="button" className="btn" disabled={!any} title={any ? 'Up to 300 frames, 960 px wide' : 'No frames yet'} onClick={() => void run('gif')}>
            <ImageDown aria-hidden="true" />
            Download GIF
          </button>
          <button
            type="button"
            className="btn"
            disabled={!any || !canVideo}
            title={!canVideo ? 'This browser can’t record video' : any ? 'Plays through in about a minute at most' : 'No frames yet'}
            onClick={() => void run('video')}
          >
            <Film aria-hidden="true" />
            Download video
          </button>
        </>
      )}
      <CopyAction text={rec.replayUrl} label="Copy link" doneLabel="Link copied" />
      {error ? (
        <div style={{ flexBasis: '100%' }}>
          <Callout tone="error" role="alert" title="The export didn’t finish">
            {error}
          </Callout>
        </div>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------- viewer */

type Mode = 'live' | 'playing' | 'paused';
type Speed = 1 | 4 | 16;
const SPEEDS: { value: Speed; label: string }[] = [
  { value: 1, label: '1×' },
  { value: 4, label: '4×' },
  { value: 16, label: '16×' },
];

/** Where the viewer is: a time on the timeline, and the entry showing (several can share a time). */
interface Pos {
  t: number;
  index: number;
}

function Viewer({ rec, tl, end }: { rec: Recording; tl: Timeline; end: number }) {
  const n = rec.entries.length;
  const recording = rec.state === 'recording';
  const [mode, setMode] = useState<Mode>(recording ? 'live' : 'paused');
  const [speed, setSpeed] = useState<Speed>(1);
  // A replay opens on its first frame.
  const [pos, setPos] = useState<Pos>(() => {
    const first = rec.entries.findIndex(hasFrame);
    const index = first >= 0 ? first : n ? 0 : -1;
    return { t: index >= 0 ? tl.offsets[index] : 0, index };
  });
  const view: Pos = mode === 'live' ? { t: end, index: n - 1 } : pos;

  // The playback loop and the key handler read the latest of these without restarting.
  const latest = useRef({ tl, end, n, recording, speed, mode, view });
  latest.current = { tl, end, n, recording, speed, mode, view };

  const put = useCallback((next: Pos) => {
    latest.current.view = next;
    setPos(next);
  }, []);

  // A recording that ends while followed stays on its last moment.
  useEffect(() => {
    if (!recording && mode === 'live') {
      put({ t: end, index: n - 1 });
      setMode('paused');
    }
  }, [recording, mode, end, n, put]);

  useEffect(() => {
    if (mode !== 'playing') return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const { tl, end, recording, speed, view } = latest.current;
      // A tab that slept doesn't leap ahead.
      const dt = Math.min(250, now - last);
      last = now;
      const t = fromPacked(tl, toPacked(tl, view.t, end) + dt * speed, end);
      if (t >= end) {
        put({ t: end, index: tl.offsets.length - 1 });
        setMode(recording ? 'live' : 'paused');
        return;
      }
      put({ t, index: Math.max(view.index, entryAt(tl, t)) });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mode, put]);

  const seek = useCallback(
    (index: number) => {
      const { tl, n } = latest.current;
      if (!n) return;
      const i = Math.max(0, Math.min(n - 1, index));
      put({ t: tl.offsets[i], index: i });
      setMode('paused');
    },
    [put],
  );

  const step = useCallback((delta: number) => seek(latest.current.view.index + delta), [seek]);

  const toggle = useCallback(() => {
    const { mode, view, end, tl, recording } = latest.current;
    if (mode !== 'paused') {
      put(view);
      setMode('paused');
      return;
    }
    // Play from the start again once a finished replay is at its end.
    if (!recording && view.t >= end - 1) put({ t: 0, index: entryAt(tl, 0) });
    setMode('playing');
  }, [put]);

  const scrub = useCallback(
    (t: number) => {
      const { tl, end, recording } = latest.current;
      if (recording && t >= end - 500) {
        setMode('live');
        return;
      }
      put({ t, index: entryAt(tl, t) });
      setMode((m) => (m === 'live' ? 'paused' : m));
    },
    [put],
  );

  const actions = useRef({ step, toggle });
  actions.current = { step, toggle };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target instanceof HTMLElement ? e.target : null;
      const tag = el?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable || (tag === 'INPUT' && (el as HTMLInputElement).type !== 'range')) return;
      // The sidebar, menus and dialogs keep their own keys.
      if (el?.closest('.side, [role="dialog"], [role="menu"]')) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        actions.current.step(e.key === 'ArrowLeft' ? -1 : 1);
      } else if (e.key === ' ' && !e.repeat && tag !== 'BUTTON' && tag !== 'A' && tag !== 'SUMMARY') {
        e.preventDefault();
        actions.current.toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const frameIndex = view.index >= 0 ? tl.frameAt[view.index] : -1;
  const frame = frameIndex >= 0 ? (rec.entries[frameIndex] as FrameBearing) : null;
  const entries = rec.entries;
  const marks = useMemo(() => marksAt(entries, tl, view.index), [entries, tl, view.index]);
  const listedIndex = view.index >= 0 ? tl.listedAt[view.index] : -1;
  const firstFrame = useMemo(() => entries.find(hasFrame), [entries]);
  const aspect = frameAspect(firstFrame) ?? 1.6;
  usePreload(rec.id, entries, tl, frameIndex);

  const firstAt = firstFrame ? tl.offsets[entries.indexOf(firstFrame)] : null;
  const empty = useMemo(
    () =>
      firstAt !== null ? (
        <>No frame before +{timecode(firstAt)}.</>
      ) : recording ? (
        <>
          <Spinner size={14} /> Waiting for the first frame
        </>
      ) : (
        <>This recording kept no frames.</>
      ),
    [firstAt, recording],
  );
  const playing = mode !== 'paused';
  const now = listedIndex >= 0 ? caption(entries[listedIndex]) : null;

  return (
    <div className="rp">
      <section className="panel panel--plain rp-screen" aria-label="Screen">
        <Stage
          recordingId={rec.id}
          frame={frame}
          marks={marks}
          aspect={aspect}
          empty={empty}
        />
        <div className="rp-bar">
          <Scrubber tl={tl} entries={entries} end={end} t={view.t} onScrub={scrub} />
          <div className="rp-controls">
            <span className="rp-controls__group">
              <button type="button" className="rp-btn" aria-label="Step back (←)" title="Step back (←)" onMouseDown={(e) => e.preventDefault()} onClick={() => step(-1)} disabled={view.index <= 0}>
                <StepBack aria-hidden="true" />
              </button>
              <button
                type="button"
                className="rp-btn rp-btn--play"
                aria-label={playing ? 'Pause (space)' : 'Play (space)'}
                title={playing ? 'Pause (space)' : 'Play (space)'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggle}
                disabled={!n}
              >
                {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
              </button>
              <button type="button" className="rp-btn" aria-label="Step forward (→)" title="Step forward (→)" onMouseDown={(e) => e.preventDefault()} onClick={() => step(1)} disabled={view.index >= n - 1}>
                <StepForward aria-hidden="true" />
              </button>
            </span>
            <span className="rp-time">
              {timecode(view.t)} <span>/ {timecode(end)}</span>
            </span>
            <span className="rp-now" title={now ?? undefined}>
              {now}
            </span>
            {recording ? (
              mode === 'live' ? (
                <span className="rp-live">
                  <Lamp tone="live" />
                  Live
                </span>
              ) : (
                <button type="button" className="btn btn--sm" onMouseDown={(e) => e.preventDefault()} onClick={() => setMode('live')}>
                  <Radio aria-hidden="true" />
                  Jump to live
                </button>
              )
            ) : null}
            {/* A click on a speed leaves the focus where it was, so space still plays. */}
            <span onMouseDown={(e) => e.preventDefault()}>
              <Segmented label="Playback speed" options={SPEEDS} value={speed} onChange={setSpeed} />
            </span>
          </div>
        </div>
      </section>
      <EntryList rec={rec} tl={tl} current={listedIndex} onSeek={seek} />
    </div>
  );
}

function frameAspect(frame: FrameBearing | undefined): number | null {
  if (!frame) return null;
  const w = frame.width ?? (frame.type === 'step' ? frame.space?.width : undefined);
  const h = frame.height ?? (frame.type === 'step' ? frame.space?.height : undefined);
  return w && h ? w / h : null;
}

/* -------------------------------------------------------------- stage */

/* Frames the viewer loaded, held so the browser keeps them ready to show again. */
const FRAME_CACHE = 80;
const frameCache = new Map<string, HTMLImageElement>();

function frameImage(url: string): HTMLImageElement {
  let img = frameCache.get(url);
  if (img) {
    frameCache.delete(url);
  } else {
    img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
  frameCache.set(url, img);
  while (frameCache.size > FRAME_CACHE) frameCache.delete(frameCache.keys().next().value!);
  return img;
}

/** Start loading the next few frames ahead of the one showing, and the one before. */
function usePreload(recordingId: string, entries: RecordingEntry[], tl: Timeline, frameIndex: number) {
  useEffect(() => {
    const names: string[] = [];
    for (let i = Math.max(0, frameIndex + 1); i < entries.length && names.length < 4; i++) {
      const e = entries[i];
      if (hasFrame(e)) names.push(e.frame);
    }
    if (frameIndex > 0) {
      const before = tl.frameAt[frameIndex - 1];
      if (before >= 0) names.push((entries[before] as FrameBearing).frame);
    }
    names.forEach((name) => frameImage(frameUrl(recordingId, name)));
  }, [recordingId, entries, tl, frameIndex]);
}

const Stage = memo(function Stage({ recordingId, frame, marks, aspect, empty }: { recordingId: string; frame: FrameBearing | null; marks: Mark[]; aspect: number; empty: ReactNode }) {
  const want = frame ? frameUrl(recordingId, frame.frame) : null;
  // The frame on screen: the last one that loaded, so a slow frame never blanks the stage.
  const [shown, setShown] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (!want) {
      setShown(null);
      return;
    }
    const img = frameImage(want);
    if (img.complete && img.naturalWidth > 0) {
      setShown(want);
      return;
    }
    let current = true;
    const done = () => {
      if (!current) return;
      if (img.naturalWidth > 0) setShown(want);
      else setFailed(want);
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
    return () => {
      current = false;
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
    };
  }, [want]);

  const fit = frameAspect(frame ?? undefined) ?? aspect;
  return (
    <div className="rp-stage" style={{ '--ar': aspect } as CSSProperties}>
      {want && shown ? (
        <div className="rp-stage__fit" style={{ '--ar': fit } as CSSProperties}>
          <img className="rp-stage__img" src={shown} alt="The screen at this point of the recording" draggable={false} />
          <MarkLayer marks={marks} />
        </div>
      ) : (
        <p className="rp-stage__empty">{want ? <Spinner size={14} /> : empty}</p>
      )}
      {want && failed === want ? <p className="rp-stage__note">This frame didn’t load.</p> : null}
    </div>
  );
});

const pct = (v: number) => `${(v * 100).toFixed(3)}%`;

/* The markers, drawn over the frame in fractions of it: a ring per click, an arrow per drag or scroll. */
function MarkLayer({ marks }: { marks: Mark[] }) {
  const uid = useId().replace(/:/g, '');
  if (!marks.length) return null;
  return (
    <svg className="rp-marks" aria-hidden="true">
      <defs>
        {(['ok', 'fail'] as const).map((tone) => (
          <marker key={tone} id={`rp-head-${tone}-${uid}`} className={`rp-head rp-head--${tone}`} viewBox="0 0 10 10" refX="6" refY="5" markerWidth="4" markerHeight="4" orient="auto">
            <path d="M0 0 L10 5 L0 10 z" />
          </marker>
        ))}
      </defs>
      {marks.map((m, i) => {
        const cls = `rp-mark${m.current ? ' is-current' : ''}${m.ok ? '' : ' is-fail'}`;
        const head = `url(#rp-head-${m.ok ? 'ok' : 'fail'}-${uid})`;
        if (m.kind === 'drag') {
          const line = { x1: pct(m.x), y1: pct(m.y), x2: pct(m.x2 ?? m.x), y2: pct(m.y2 ?? m.y) };
          return (
            <g key={i} className={cls}>
              <line {...line} className="rp-mark__halo" />
              <line {...line} className="rp-mark__line" markerEnd={head} />
              <svg x={pct(m.x)} y={pct(m.y)}>
                <circle r="4" className="rp-mark__dot" />
              </svg>
            </g>
          );
        }
        return (
          <g key={i} className={cls}>
            <svg x={pct(m.x)} y={pct(m.y)}>
              {m.kind === 'move' ? (
                <circle r="6" className="rp-mark__ring" />
              ) : m.kind === 'scroll' ? (
                <>
                  <circle r="12" className="rp-mark__halo" />
                  <circle r="12" className="rp-mark__ring" />
                  <line x1={DIRS[m.dir ?? 'down'][0] * 16} y1={DIRS[m.dir ?? 'down'][1] * 16} x2={DIRS[m.dir ?? 'down'][0] * 32} y2={DIRS[m.dir ?? 'down'][1] * 32} className="rp-mark__line" markerEnd={head} />
                </>
              ) : (
                <>
                  {m.current ? <circle r="14" className="rp-mark__pulse" /> : null}
                  <circle r="14" className="rp-mark__halo" />
                  <circle r="14" className={`rp-mark__ring${m.button === 'right' ? ' is-dashed' : ''}`} />
                  {Array.from({ length: (m.count ?? 1) - 1 }, (_, k) => (
                    <circle key={k} r={20 + k * 6} className="rp-mark__ring rp-mark__ring--extra" />
                  ))}
                  <circle r="3" className="rp-mark__dot" />
                </>
              )}
            </svg>
          </g>
        );
      })}
    </svg>
  );
}

/* ----------------------------------------------------------- scrubber */

function Scrubber({ tl, entries, end, t, onScrub }: { tl: Timeline; entries: RecordingEntry[]; end: number; t: number; onScrub: (t: number) => void }) {
  const span = Math.max(1, end);
  const at = `${Math.min(100, (t / span) * 100)}%`;
  return (
    <div className="rp-scrub">
      {/* The rail sits half a thumb in from each side, where the range input's own values run. */}
      <div className="rp-scrub__rail">
        <div className="rp-scrub__track">
          <div className="rp-scrub__fill" style={{ width: at }} />
        </div>
        <Ticks tl={tl} entries={entries} span={Math.round(span / 1000) * 1000 || 1} />
        <div className="rp-scrub__head" style={{ left: at }} />
      </div>
      <input
        type="range"
        className="rp-scrub__input"
        min={0}
        max={Math.round(span)}
        step={1}
        value={Math.round(Math.min(t, span))}
        onChange={(e) => onScrub(Number(e.target.value))}
        aria-label="Timeline"
        aria-valuetext={`${timecode(t)} of ${timecode(span)}`}
      />
    </div>
  );
}

/** A tick on the scrubber for each step, run and command (frames and logs would only be noise). */
const Ticks = memo(function Ticks({ tl, entries, span }: { tl: Timeline; entries: RecordingEntry[]; span: number }) {
  return (
    <div className="rp-ticks" aria-hidden="true">
      {tl.listed.map((i) => {
        const e = entries[i];
        if (e.type === 'log' || e.type === 'end') return null;
        const bad = (e.type === 'step' && !e.ok) || (e.type === 'command' && (e.exitCode !== 0 || e.timedOut)) || (e.type === 'run' && e.event === 'finished' && e.verdict !== 'done');
        return <span key={i} className={`rp-tick rp-tick--${e.type}${bad ? ' is-bad' : ''}`} style={{ left: `${Math.min(100, (tl.offsets[i] / span) * 100)}%` }} />;
      })}
    </div>
  );
});

/* ------------------------------------------------------------ entries */

const EntryList = memo(function EntryList({ rec, tl, current, onSeek }: { rec: Recording; tl: Timeline; current: number; onSeek: (index: number) => void }) {
  const box = useRef<HTMLOListElement>(null);
  // Keep the current entry in view, scrolling the list only (never the page).
  useEffect(() => {
    const list = box.current;
    const row = list?.querySelector<HTMLElement>('[data-current]');
    if (!list || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top - 8;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 8;
  }, [current]);

  const frames = rec.entries.length - tl.listed.length;
  return (
    <section className="rp-side" aria-label="Timeline">
      <div className="rp-side__head">
        <h2 className="h2">Timeline</h2>
        <span className="muted small">{plural(tl.listed.length, 'entry', 'entries')}</span>
      </div>
      {tl.listed.length === 0 ? (
        <p className="rp-side__empty">{frames ? `Only frames so far: ${plural(frames, 'frame')}, and no steps, commands or logs.` : rec.state === 'recording' ? 'Nothing recorded yet. Entries show up here as they happen.' : 'Nothing was recorded.'}</p>
      ) : (
        <ol className="rp-list" ref={box}>
          {tl.listed.map((i) => (
            <EntryRow key={rec.entries[i].seq} entry={rec.entries[i]} index={i} offset={tl.offsets[i]} current={i === current} onSeek={onSeek} />
          ))}
        </ol>
      )}
      <p className="rp-side__keys">
        <kbd>←</kbd> <kbd>→</kbd> step, <kbd>space</kbd> plays
      </p>
    </section>
  );
});

/** Long text that opens on demand, and renders only once opened. */
function Expand({ summary, children }: { summary: ReactNode; children: () => ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="rp-entry__more" onToggle={(e) => setOpen(e.currentTarget.open)} onClick={(e) => e.stopPropagation()}>
      <summary>{summary}</summary>
      {open ? children() : null}
    </details>
  );
}

function entryHead(e: RecordingEntry): { tone: Parameters<typeof Lamp>[0]['tone']; title: ReactNode; bad?: boolean } {
  switch (e.type) {
    case 'step':
      return { tone: !e.ok ? 'refused' : stepAction(e) === 'screenshot' ? 'hollow' : 'accent', title: stepLabel(e), bad: !e.ok };
    case 'run':
      return e.event === 'started'
        ? { tone: 'amber', title: 'Agent run started' }
        : { tone: VERDICT_TONE[e.verdict ?? 'error'], title: verdictLabel(e), bad: e.verdict !== 'done' && e.verdict !== 'incomplete' };
    case 'command': {
      const bad = e.timedOut || e.exitCode !== 0;
      return { tone: bad ? 'refused' : 'off', title: <code>$ {e.command}</code>, bad };
    }
    case 'log':
      return { tone: 'hollow', title: `Log, ${plural(e.lines.length, 'line')}` };
    case 'end':
      return { tone: 'off', title: 'Recording ended' };
    default:
      return { tone: 'off', title: e.type };
  }
}

const EntryRow = memo(function EntryRow({ entry, index, offset, current, onSeek }: { entry: RecordingEntry; index: number; offset: number; current: boolean; onSeek: (index: number) => void }) {
  const { tone, title, bad } = entryHead(entry);
  let body: ReactNode = null;
  switch (entry.type) {
    case 'step': {
      const reason = stepAction(entry) === 'finish' && typeof entry.input?.reason === 'string' ? entry.input.reason : null;
      body = (
        <>
          {entry.note ? <p className="rp-entry__note">{entry.note}</p> : null}
          {reason ? <p className="rp-entry__note">{reason}</p> : null}
          {entry.error ? <p className="rp-entry__err">{entry.error}</p> : null}
        </>
      );
      break;
    }
    case 'run':
      body =
        entry.event === 'started' ? (
          <>
            <p className="rp-entry__note">{entry.goal}</p>
            <p className="rp-entry__meta">{entry.model}</p>
          </>
        ) : (
          <>
            {entry.reason ? <p className="rp-entry__note">{entry.reason}</p> : null}
            {entry.error ? (
              <p className="rp-entry__err">
                {entry.error.message} <span className="mono">({entry.error.code})</span>
              </p>
            ) : null}
          </>
        );
      break;
    case 'command': {
      const lines = entry.output ? entry.output.replace(/\n$/, '').split('\n').length : 0;
      body = (
        <>
          <p className={`rp-entry__meta${bad ? ' is-bad' : ''}`}>
            {entry.timedOut ? 'stopped at its timeout' : entry.exitCode === null ? 'no exit code' : `exit ${entry.exitCode}`} · {duration(entry.durationMs)}
          </p>
          {lines ? (
            <Expand summary={`Output, ${plural(lines, 'line')}`}>
              {() => (
                <>
                  <pre className="rp-pre">{entry.output}</pre>
                  {entry.truncated ? <p className="rp-entry__meta">The output was cut short.</p> : null}
                </>
              )}
            </Expand>
          ) : null}
        </>
      );
      break;
    }
    case 'log':
      body = entry.lines.length ? <Expand summary="Show lines">{() => <pre className="rp-pre">{entry.lines.join('\n')}</pre>}</Expand> : null;
      break;
    case 'end':
      body = entry.reason ? <p className="rp-entry__meta">{entry.reason.replace(/[-_]/g, ' ')}</p> : null;
      break;
  }
  return (
    <li className={`rp-entry${current ? ' is-current' : ''}${bad ? ' is-bad' : ''}`} data-current={current || undefined} onClick={() => onSeek(index)}>
      <button
        type="button"
        className="rp-entry__seek"
        aria-current={current ? 'step' : undefined}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onSeek(index);
        }}
      >
        <time className="rp-entry__t">+{timecode(offset)}</time>
        <Lamp tone={tone} />
        <span className="rp-entry__what">{title}</span>
      </button>
      {body ? <div className="rp-entry__body">{body}</div> : null}
    </li>
  );
});
